/**
 * Global date / time formatting driven by the user's Account Settings.
 *
 * The preferences come from the login response (`account_settings`) and from
 * Settings → Account. They are persisted so a page reload keeps them, and every
 * date shown in the app should go through formatDate / formatTime / formatDateTime.
 */

const STORAGE_KEY = 'robitest_datetime_prefs';
export const DATETIME_PREFS_EVENT = 'robitest:datetime-prefs-changed';

export const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', 'DD-MMM-YYYY'];
export const TIME_FORMATS = ['12-hour (AM/PM)', '24-hour'];

// Application default is ISO 8601 (YYYY-MM-DD, 24-hour) until the user picks otherwise
const DEFAULT_PREFS = { dateFormat: 'YYYY-MM-DD', timeFormat: '24-hour' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const normalizePrefs = (prefs) => ({
  dateFormat: DATE_FORMATS.includes(prefs?.dateFormat) ? prefs.dateFormat : DEFAULT_PREFS.dateFormat,
  timeFormat: TIME_FORMATS.includes(prefs?.timeFormat) ? prefs.timeFormat : DEFAULT_PREFS.timeFormat,
});

const readStoredPrefs = () => {
  try {
    return normalizePrefs(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return { ...DEFAULT_PREFS };
  }
};

let currentPrefs = readStoredPrefs();

export const getDateTimePrefs = () => currentPrefs;

/**
 * Apply account settings as returned by the backend
 * ({ date_format, time_format }). Missing values fall back to the defaults,
 * so calling this with null on login resets a previous user's choice.
 */
export const setDateTimePrefs = (accountSettings) => {
  currentPrefs = normalizePrefs({
    dateFormat: accountSettings?.date_format,
    timeFormat: accountSettings?.time_format,
  });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentPrefs));
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DATETIME_PREFS_EVENT, { detail: currentPrefs }));
  }
};

/**
 * Parse a timestamp from the API or WebSocket into a Date.
 * - ISO strings with an offset ("2026-02-02T18:20:28.470149+05:30") keep their offset;
 *   sub-millisecond digits are trimmed so every browser parses them.
 * - Naive datetime strings (no timezone) are treated as UTC — the backend sends
 *   UTC values without a "Z" suffix over WebSocket.
 * - Date-only strings ("2026-02-02") are treated as a local calendar day so they
 *   don't shift by a day in negative-offset timezones.
 */
export const parseTimestamp = (timestamp) => {
  if (timestamp == null || timestamp === '') return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'number') return Number.isNaN(timestamp) ? null : new Date(timestamp);

  const str = String(timestamp).trim().replace(/(\.\d{3})\d+/, '$1');
  if (!str) return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));

  if (/[zZ]$|[+-]\d{2}(:?\d{2})?$/.test(str)) return new Date(str);

  const normalized = str.includes('T') ? str : str.replace(' ', 'T');
  return new Date(`${normalized}Z`);
};

const toValidDate = (value) => {
  const date = parseTimestamp(value);
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const pad = (n) => String(n).padStart(2, '0');

const formatDatePart = (date, dateFormat) => {
  const dd = pad(date.getDate());
  const mm = pad(date.getMonth() + 1);
  const yyyy = date.getFullYear();
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`;
    case 'DD/MM/YYYY':
      return `${dd}/${mm}/${yyyy}`;
    case 'YYYY/MM/DD':
      return `${yyyy}/${mm}/${dd}`;
    case 'DD-MMM-YYYY':
      return `${dd}-${MONTHS[date.getMonth()]}-${yyyy}`;
    case 'YYYY-MM-DD':
    default:
      return `${yyyy}-${mm}-${dd}`;
  }
};

const formatTimePart = (date, timeFormat, withSeconds) => {
  const minutes = pad(date.getMinutes());
  const seconds = withSeconds ? `:${pad(date.getSeconds())}` : '';
  if (timeFormat === '24-hour') {
    return `${pad(date.getHours())}:${minutes}${seconds}`;
  }
  const hours = date.getHours();
  const suffix = hours < 12 ? 'AM' : 'PM';
  return `${hours % 12 || 12}:${minutes}${seconds} ${suffix}`;
};

/** Date only, in the user's date format. Returns `fallback` for empty/invalid input. */
export const formatDate = (value, fallback = '') => {
  const date = toValidDate(value);
  return date ? formatDatePart(date, currentPrefs.dateFormat) : fallback;
};

/** Time only, in the user's 12/24-hour format. */
export const formatTime = (value, { seconds = false, fallback = '' } = {}) => {
  const date = toValidDate(value);
  return date ? formatTimePart(date, currentPrefs.timeFormat, seconds) : fallback;
};

/** Date and time, in the user's formats. */
export const formatDateTime = (value, { seconds = false, fallback = '' } = {}) => {
  const date = toValidDate(value);
  if (!date) return fallback;
  return `${formatDatePart(date, currentPrefs.dateFormat)}, ${formatTimePart(date, currentPrefs.timeFormat, seconds)}`;
};
